"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  CONNECTION_LOST_CODE,
  handleError,
  type ErrorRecovery,
} from "@/lib/handle-error";

export interface CallErrorState {
  error: string | null;
  code?: string;
  recovery: ErrorRecovery;
  reportable: boolean;
  errorEventId?: string;
}

interface CallFailure extends CallErrorState {
  error: string;
}

export const NO_CALL_ERROR: CallErrorState = {
  error: null,
  recovery: "none",
  reportable: false,
  errorEventId: undefined,
};

export function describeCallFailure(err: unknown): CallFailure | null {
  const handled = handleError(err, { display: "silent" });
  if (handled.action === "ignore") return null;
  return {
    error: handled.message,
    code: handled.code,
    recovery: handled.recovery,
    reportable: handled.reportable,
    errorEventId: handled.errorEventId,
  };
}

export function useMountedRef() {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return mountedRef;
}

interface TrackedCallHandlers<TResult> {
  onSuccess: (result: TResult) => void;
  onError: (errorMessage: string) => void;
  onSettled?: () => void;
}

interface TrackedCallOptions {
  retryable?: boolean;
}

function recordFailure(
  err: unknown,
  setErrorState: (state: CallErrorState) => void,
  onError: (errorMessage: string) => void,
  recovery?: ErrorRecovery,
) {
  const failure = describeCallFailure(err);
  if (!failure) return;
  setErrorState(recovery ? { ...failure, recovery } : failure);
  onError(failure.error);
}

function useCallPerformer() {
  const [errorState, setErrorState] = useState<CallErrorState>(NO_CALL_ERROR);
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useMountedRef();

  const clearError = useCallback(() => setErrorState(NO_CALL_ERROR), []);

  const perform = useCallback(
    async <TResult>(
      action: () => Promise<TResult>,
      handlers: TrackedCallHandlers<TResult>,
    ): Promise<TResult | undefined> => {
      if (mountedRef.current) {
        setErrorState(NO_CALL_ERROR);
        setIsLoading(true);
      }
      const settle = () => {
        if (!mountedRef.current) return;
        setIsLoading(false);
        handlers.onSettled?.();
      };
      let result: TResult;
      try {
        result = await action();
      } catch (err: unknown) {
        if (mountedRef.current) recordFailure(err, setErrorState, handlers.onError);
        settle();
        return undefined;
      }
      try {
        if (mountedRef.current) handlers.onSuccess(result);
      } catch (err: unknown) {
        recordFailure(err, setErrorState, handlers.onError, "none");
      }
      settle();
      return result;
    },
    [mountedRef],
  );

  return { errorState, isLoading, clearError, perform };
}

export function useTrackedCall({ retryable = false }: TrackedCallOptions = {}) {
  const { errorState, isLoading, clearError, perform } = useCallPerformer();
  const lastCallRef = useRef<(() => Promise<unknown>) | null>(null);
  const retryingRef = useRef(false);

  const run = useCallback(
    <TResult>(
      action: () => Promise<TResult>,
      handlers: TrackedCallHandlers<TResult>,
    ): Promise<TResult | undefined> => {
      lastCallRef.current = () => perform(action, handlers);
      return perform(action, handlers);
    },
    [perform],
  );

  const retry = useCallback(() => {
    const replay = lastCallRef.current;
    if (!replay || retryingRef.current) return;
    retryingRef.current = true;
    void replay().finally(() => {
      retryingRef.current = false;
    });
  }, []);

  const canRetry =
    retryable &&
    errorState.recovery === "retry" &&
    errorState.code !== CONNECTION_LOST_CODE;

  return {
    ...errorState,
    isLoading,
    clearError,
    run,
    retry: canRetry ? retry : undefined,
  };
}
